/**
 * Shopping cart page
 * Follows the unified UX pattern with stats, modern design
 */

import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { useMemo } from 'react';
import { StatsWidget, StatItem } from '../components/common/StatsWidget';
import {
  ShoppingCart,
  Package,
  DollarSign,
  Trash2,
  ArrowLeft,
  ShoppingBag,
  HelpCircle,
  CreditCard,
  Lock,
} from 'lucide-react';

export default function CartPage(): JSX.Element {
  const { items, removeFromCart, getTotal, getFormattedTotal } = useCart();
  const { currentUser } = useAuth();
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();

  const total = getTotal();
  const formattedTotal = getFormattedTotal();

  // Calculate stats
  const stats = useMemo(() => {
    return {
      itemCount: items.length,
      total: total,
    };
  }, [items, total]);

  // Stat items for the widget
  const statItems: StatItem[] = useMemo(() => [
    {
      id: 'items',
      label: 'Items in Cart',
      value: stats.itemCount,
      icon: Package,
      variant: 'primary',
    },
    {
      id: 'total',
      label: 'Cart Total',
      value: formattedTotal,
      icon: DollarSign,
      variant: 'success',
    },
  ], [stats, formattedTotal]);

  function handleCheckout(): void {
    if (currentUser) {
      navigate('/checkout');
    } else {
      // Redirect to login with checkout as return destination
      navigate('/login?redirect=' + encodeURIComponent('/checkout'));
    }
  }

  // Empty cart state
  if (items.length === 0) {
    return (
      <div className="container-custom py-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100">
            Shopping Cart
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Review your items before checkout
          </p>
        </div>

        {/* Empty State */}
        <div className="card p-12 text-center max-w-lg mx-auto">
          <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-full inline-block mb-6">
            <ShoppingCart size={48} className="text-gray-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">
            Your Cart is Empty
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Browse our collection of strategy guides and add some products to your cart!
          </p>
          <Link to="/products" className="btn btn-primary inline-flex items-center gap-2">
            <ShoppingBag size={18} />
            Browse Products
          </Link>
        </div>

        {/* Help CTA */}
        <section className="mt-12 bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 rounded-xl p-6 border border-primary-100 dark:border-primary-800">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white dark:bg-gray-800 rounded-full shadow-sm">
                <HelpCircle size={24} className="text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                  Need help finding the right guide?
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Visit our help center for recommendations and buying guides
                </p>
              </div>
            </div>
            <Link to="/help" className="btn btn-secondary whitespace-nowrap">
              View Help Center
            </Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="container-custom py-12">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100">
          Shopping Cart
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Review your items before checkout
        </p>
      </div>

      {/* Stats Section */}
      <section className="mb-8">
        <StatsWidget stats={statItems} columns={2} />
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Cart Items */}
        <div className="lg:col-span-2 space-y-4">
          {items.map((item) => (
            <div key={item.productId} className="card p-6 hover:shadow-md transition-shadow">
              <div className="flex gap-4">
                {/* Product Icon */}
                <div className="flex-shrink-0">
                  <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                    <Package size={24} className="text-primary-600 dark:text-primary-400" />
                  </div>
                </div>

                {/* Product Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">
                    {item.product?.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-3 line-clamp-2">
                    {item.product?.shortDescription}
                  </p>

                  <div className="flex items-center justify-between">
                    <span className="text-xl font-bold text-primary-600 dark:text-primary-400">
                      {formatPrice(item.price.amount, item.price.currency)}
                    </span>

                    <button
                      onClick={() => removeFromCart(item.productId)}
                      className="inline-flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
                    >
                      <Trash2 size={16} />
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Continue Shopping Link */}
          <Link
            to="/products"
            className="inline-flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium mt-4"
          >
            <ArrowLeft size={16} />
            Continue Shopping
          </Link>
        </div>

        {/* Summary */}
        <div className="lg:col-span-1">
          <div className="card p-6 sticky top-24">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-6 flex items-center gap-2">
              <ShoppingBag size={20} />
              Order Summary
            </h2>

            <div className="space-y-3 mb-6">
              <div className="flex justify-between text-gray-600 dark:text-gray-400">
                <span>Items ({items.length})</span>
                <span>{formattedTotal}</span>
              </div>

              <div className="flex justify-between text-gray-600 dark:text-gray-400">
                <span>Delivery</span>
                <span className="text-green-600 dark:text-green-400">Digital - Instant</span>
              </div>

              <div className="flex justify-between font-bold text-lg pt-4 border-t border-gray-200 dark:border-gray-700">
                <span>Total</span>
                <span className="text-primary-600 dark:text-primary-400">
                  {formattedTotal}
                </span>
              </div>
            </div>

            <button
              onClick={handleCheckout}
              className="w-full btn btn-primary mb-4 inline-flex items-center justify-center gap-2"
            >
              {currentUser ? (
                <>
                  <CreditCard size={18} />
                  Proceed to Checkout
                </>
              ) : (
                <>
                  <Lock size={18} />
                  Login to Checkout
                </>
              )}
            </button>

            {/* Security Note */}
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 justify-center">
              <Lock size={12} />
              <span>Secure checkout with SSL encryption</span>
            </div>

            {/* Payment Methods */}
            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center mb-3">
                Accepted Payment Methods
              </p>
              <div className="flex justify-center gap-3">
                <div className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded text-xs font-medium text-gray-600 dark:text-gray-400">
                  Stripe
                </div>
                <div className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded text-xs font-medium text-gray-600 dark:text-gray-400">
                  PayPal
                </div>
                <div className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded text-xs font-medium text-gray-600 dark:text-gray-400">
                  Cards
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Help CTA */}
      <section className="mt-12 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-750 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white dark:bg-gray-700 rounded-full shadow-sm">
              <HelpCircle size={24} className="text-gray-600 dark:text-gray-400" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                Have questions about your order?
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Check our FAQ for payment, delivery, and refund information
              </p>
            </div>
          </div>
          <Link to="/help" className="btn btn-secondary whitespace-nowrap">
            View Help Center
          </Link>
        </div>
      </section>
    </div>
  );
}
